/**
 * Analysis Config Store (Zustand)
 *
 * Manages global state for AI analysis configuration.
 * Replaces the local state in PicksView for config management.
 */

import { applyCascadeDisableAll } from '@config/toggleDependencies';
import type { AnalysisConfig as SharedAnalysisConfig } from '@parlay-picker/shared/analysisConfig';
import { ApiError } from '@parlay-picker/shared/api/httpClient';
import { adjustmentsFromBody, type ConfigAdjustment } from '@parlay-picker/shared/tierInputPolicy';
import type { AnalysisConfig } from '@views/picks/constants';
import { DEFAULT_ANALYSIS_CONFIG } from '@views/picks/constants';
import { deepMergeConfig } from '@views/picks/utils';
import { create } from 'zustand';

// AuthFetch type from AuthContext
type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

interface AnalysisConfigState {
  // Config state
  config: AnalysisConfig;
  savedConfig: AnalysisConfig;

  // User history stats (for config panel threshold check)
  settledLegCount: number;

  // Computed values
  hasUnsavedChanges: () => boolean;

  // Actions
  setConfig: (config: AnalysisConfig | ((prev: AnalysisConfig) => AnalysisConfig)) => void;
  /** Saves and returns what the server trimmed to fit the plan (empty when nothing). */
  saveConfig: (authFetch: AuthFetch) => Promise<ConfigAdjustment[]>;
  loadFromUser: (userConfig: SharedAnalysisConfig | null) => void;
  loadFromLocalStorage: () => void;
  setSettledLegCount: (count: number) => void;
}

// Load initial config from localStorage
function getInitialConfig(): AnalysisConfig {
  if (typeof window === 'undefined') return DEFAULT_ANALYSIS_CONFIG;
  const saved = localStorage.getItem('analysisConfig');
  try {
    return saved
      ? deepMergeConfig(DEFAULT_ANALYSIS_CONFIG, JSON.parse(saved))
      : DEFAULT_ANALYSIS_CONFIG;
  } catch {
    return DEFAULT_ANALYSIS_CONFIG;
  }
}

export const useAnalysisConfigStore = create<AnalysisConfigState>((set, get) => ({
  // Initial state from localStorage
  config: getInitialConfig(),
  savedConfig: getInitialConfig(),
  settledLegCount: 0,

  // Computed: check if current config differs from saved
  hasUnsavedChanges: () => {
    const { config, savedConfig } = get();
    return JSON.stringify(config) !== JSON.stringify(savedConfig);
  },

  // Update config (with optional functional update)
  setConfig: (configOrUpdater) => {
    set((state) => {
      const newConfig =
        typeof configOrUpdater === 'function' ? configOrUpdater(state.config) : configOrUpdater;

      // Also persist to localStorage for session persistence
      localStorage.setItem('analysisConfig', JSON.stringify(newConfig));

      return { config: newConfig };
    });
  },

  // Save config to database via authFetch (also syncs presets)
  saveConfig: async (authFetch: AuthFetch) => {
    const { config } = get();
    const res = await authFetch('/api/auth/analysis-config', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ analysisConfig: config }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new ApiError(data.error || 'Failed to save config', res.status, undefined, data);
    }

    // The server stores the config cleaned to the plan; when it changed anything, take its copy.
    const data = await res.json().catch(() => ({}));
    const adjusted = adjustmentsFromBody(data);
    const stored = data?.user?.analysisConfig;
    if (adjusted.length > 0 && stored) get().loadFromUser(stored);
    else set({ savedConfig: config });

    // Also sync presets (the active preset's config was updated in-place)
    const { usePresetsStore } = await import('./presetsStore');
    usePresetsStore
      .getState()
      .syncToServer(authFetch)
      .catch(() => {});
    return adjusted;
  },

  // Load config from user's database record
  loadFromUser: (userConfig) => {
    // The server is the source of truth for a signed-in user: with nothing stored, start from the
    // defaults rather than whatever an earlier session left in browser storage.
    const raw = userConfig
      ? deepMergeConfig(DEFAULT_ANALYSIS_CONFIG, userConfig as Partial<AnalysisConfig>)
      : DEFAULT_ANALYSIS_CONFIG;
    // Cascade-on-load: normalize stale state from before the dependency system. No limit clamp here:
    // the server returns the stored config already cleaned to the user's plan.
    const merged = applyCascadeDisableAll(raw);
    set({ config: merged, savedConfig: merged });
    localStorage.setItem('analysisConfig', JSON.stringify(merged));
  },

  // Load config from localStorage (for non-logged-in users or initial load)
  loadFromLocalStorage: () => {
    const config = getInitialConfig();
    set({ config, savedConfig: config });
  },

  // Update settled leg count (from PicksView when legs data loads)
  setSettledLegCount: (count) => set({ settledLegCount: count }),
}));

// Selector hooks for common patterns
export const useAnalysisConfig = (): AnalysisConfig => useAnalysisConfigStore((s) => s.config);
